/**
 * Studio — Layout route (Wave 3 b73 rebuild).
 *
 * Wraps all /studio/* child routes with ContentHeader + tab navigation.
 *
 * Tabs (in order): Models, Tools, Functions, Skills, Prompts
 *   - Models    — read-only catalog of Argus brands assigned to this
 *                 tenant (replaces the dropped /models rail item).
 *   - Tools     — tenant-authored tools (workspace_tools where
 *                 organization_id = me).
 *   - Functions — tenant-authored custom functions backed by
 *                 tenant_functions (Wave 3 Stream 1 — PRs #57 / #58 /
 *                 #377). Surfaces the same list previously only
 *                 reachable via /connections/functions; reuses the
 *                 exported `FunctionsPage` so behaviour stays in sync.
 *   - Skills    — tenant-authored skills (studio_skills).
 *   - Prompts   — tenant-authored prompts UNION sysadmin-authored
 *                 prompts flagged visible_to_tenant=true (badged
 *                 "System").
 *
 * Models is the index tab (/studio renders it). Tools moves to
 * /studio/tools to make room.
 *
 * Functions was the missing entry — schema/API/route shipped via
 * Wave 3 Stream 1 but the TabBar slot + a `/studio/functions` shim
 * never landed, leaving the surface 404-only and the canonical-6 system
 * "Function Helper" agent without a place to hand off into. This file
 * was the only TabBar source-of-truth, so adding the entry here +
 * mirroring it into `STUDIO_TABS` (extracted to make the slot pinnable
 * from a unit test) wires the rail end-to-end.
 */

import { createFileRoute, Outlet, redirect, useLocation, useNavigate } from '@tanstack/react-router';
import { ContentHeader } from '@/components/content-header';
import {
  Plus, Code, Database, Globe, Plug, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useHasPermission } from '@/lib/use-has-permission';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
// STUDIO_TABS is the single source of truth for Studio's primary tab
// bar. Lives in a sibling `-`-prefixed pure-logic file so unit tests
// can pin the entry set without dragging in createFileRoute / React.
// Re-exported below for backwards compatibility with anywhere that
// might already import from this route module.
import { STUDIO_TABS } from './-studio-tabs';
export { STUDIO_TABS };

const OPENAPI_DEFERRED_TITLE = 'OpenAPI tool import is deferred for V1.';

export const Route = createFileRoute('/studio')({
  // fix-wave-4 #1030 (STUDIO-PERM-001) — Pre-fix the root layout
  // checked auth via `useEffect`, so an unauthenticated user landing
  // on /studio briefly rendered the layout shell BEFORE the navigation
  // to /login fired. That leaked the IA structure (tabs + brand) to
  // pen-testers and made the route appear to "load without redirect"
  // to the cycle-3.6 matrix probe.
  //
  // beforeLoad runs synchronously during route resolution, BEFORE the
  // component mounts. Throwing `redirect()` from beforeLoad turns the
  // navigation into a hard redirect with no intermediate paint. We
  // gate on the presence of an access token in localStorage — the same
  // signal the root layout used, just enforced earlier in the lifecycle.
  beforeLoad: () => {
    if (typeof window !== 'undefined') {
      const accessToken = window.localStorage.getItem('accessToken');
      if (!accessToken) {
        throw redirect({ to: '/login' });
      }
    }
  },
  component: StudioLayout,
});

/**
 * The Tools "Register Tool" dropdown is the only action that stays in
 * the layout because it routes to a separate full-page editor at
 * /studio/tools/new. Skills + Prompts use in-page Sheets, so their
 * "New X" button is rendered inside their respective pages and
 * triggers the Sheet directly. Models is read-only.
 */
function StudioLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const canWriteTools = useHasPermission('write', 'tools');

  // Derived from STUDIO_TABS — every declared tab path PLUS its
  // trailing-slash variant. Keeping this derived (not a parallel list)
  // means new tabs only need to be added in one place: STUDIO_TABS.
  const TAB_PATHS = STUDIO_TABS.flatMap((t) => [t.path, `${t.path}/`]);
  const isTabPage = TAB_PATHS.includes(location.pathname);

  const isToolsTab =
    location.pathname === '/studio/tools' || location.pathname === '/studio/tools/';

  // #148 — Single Register Tool button now opens the new source picker
  // at `/studio/tools/new` (?type=picker by default). The previous
  // dropdown listed source types directly; users now see the same
  // chooser as a full page with richer descriptions for each option.
  //
  // For muscle memory: the dropdown is kept as a quick-jump shortcut
  // so power users can still skip the picker for the two flows they
  // hit most often (HTTP + Database).
  const renderActions = () => {
    if (!isToolsTab) return null;
    if (!canWriteTools) return null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" className="gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Register Tool
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>Choose a tool source</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() =>
              navigate({ to: '/studio/tools/new', search: { type: 'mcp' } })
            }
            className="flex items-start gap-2 cursor-pointer"
            title="Import a tool from a registered MCP server."
          >
            <Plug className="h-4 w-4 mt-0.5 text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-medium">MCP Server tool</p>
              <p className="text-[11px] text-muted-foreground">
                Pick a tool from a registered MCP server's manifest.
              </p>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled
            className="flex items-start gap-2"
            title={OPENAPI_DEFERRED_TITLE}
          >
            <Code className="h-4 w-4 mt-0.5 text-cyan-600" />
            <div className="flex-1">
              <p className="text-sm font-medium">OpenAPI / REST endpoint</p>
              <p className="text-[11px] text-muted-foreground">
                Deferred for V1. Use MCP, Custom HTTP, or Database tools.
              </p>
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() =>
              navigate({ to: '/studio/tools/new', search: { type: 'http' } })
            }
            className="flex items-start gap-2 cursor-pointer"
          >
            <Globe className="h-4 w-4 mt-0.5 text-emerald-600" />
            <div className="flex-1">
              <p className="text-sm font-medium">Custom HTTP / API tool</p>
              <p className="text-[11px] text-muted-foreground">
                Call any REST endpoint with auth — Monaco + Tool Helper.
              </p>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              navigate({ to: '/studio/tools/new', search: { type: 'database' } })
            }
            className="flex items-start gap-2 cursor-pointer"
          >
            <Database className="h-4 w-4 mt-0.5 text-violet-600" />
            <div className="flex-1">
              <p className="text-sm font-medium">Database query</p>
              <p className="text-[11px] text-muted-foreground">
                Parameterized SQL via a registered connection.
              </p>
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => navigate({ to: '/studio/functions' })}
            className="flex items-start gap-2 cursor-pointer"
            title="Open Studio → Functions to author + deploy a custom function."
          >
            <Sparkles className="h-4 w-4 mt-0.5 text-amber-600" />
            <div className="flex-1">
              <p className="text-sm font-medium">
                Custom Function{' '}
                <span className="text-[10px] text-muted-foreground">
                  (Studio → Functions)
                </span>
              </p>
              <p className="text-[11px] text-muted-foreground">
                JavaScript / Python functions live under Studio → Functions.
              </p>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {isTabPage && (
        <ContentHeader
          title="Studio"
          // STUDIO_TABS is `as const` so the inferred element type is
          // narrower than ContentHeader's tab signature expects; spread
          // through a mutable copy.
          tabs={STUDIO_TABS.map((t) => ({ ...t }))}
          actions={renderActions()}
        />
      )}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
